import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner, Text, YStack } from "tamagui";
import { RiCheckboxCircleFill, RiCloseCircleFill } from "react-icons/ri";
import { colors } from "@styles/colors";
import { completeIPassAuthorization } from "@utils/ipassMoney";

/**
 * iPASS 授權完成後的落地頁（流程 C 的最後一段）。
 *
 * 一卡通在客戶同意授權後會把畫面導回這裡，並在 query string 補上
 * `TransactionId`（規格明定保留字，字首大寫）。拿它去換 regKey。
 *
 * 規格 §四：confirmUrl 必須是 https 且採可信任憑證；若 confirmUrlType 用 SERVER，
 * route 結尾還必須是 OrderResult——目前用 CLIENT，所以這裡自己打 A2。
 */
export default function IPassConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const transactionId = params.get("TransactionId") || params.get("transactionId") || "";
  const orderId = params.get("orderId") || "";
  // 後端 A1 時簽進 confirmUrl 的授權憑證（誰發動的），原樣轉回後端
  const authState = params.get("state") || "";
  // 授權是從哪一頁被導出來的，完成後回那裡（只收站內路徑，避免開放式跳轉）
  const rawReturn = params.get("returnTo") || "";
  const returnTo = rawReturn.startsWith("/") && !rawReturn.startsWith("//") ? rawReturn : "/gasShop/cart";
  const [state, setState] = useState<"loading" | "ok" | "fail">("loading");
  const [message, setMessage] = useState("");
  // StrictMode 下 effect 會跑兩次，A2 是有副作用的（建立 regKey），擋掉第二次
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!transactionId) {
      setState("fail");
      setMessage("沒有取得一卡通交易序號，請回到訂單重新選擇付款方式");
      return;
    }
    // 歸戶對象在後端簽的 state 裡（不依賴後端記憶體，部署到多台也找得到）
    completeIPassAuthorization({ transactionId, state: authState })
      .then((r) => {
        setState(r.authorized ? "ok" : "fail");
        setMessage(r.message || "");
      })
      .catch((e) => {
        setState("fail");
        setMessage(e instanceof Error ? e.message : "授權確認失敗");
      });
  }, [transactionId, orderId, authState]);

  return (
    <YStack f={1} ai="center" jc="center" gap={16} p="$4" background={colors.bg}>
      {state === "loading" && (
        <>
          <Spinner size="large" color={colors.primary} />
          <Text color={colors.text}>正在確認授權結果…</Text>
        </>
      )}

      {state === "ok" && (
        <>
          <RiCheckboxCircleFill size={64} color={colors.green} />
          <Text fontSize={20} fontWeight="700" color={colors.text}>
            已完成授權
          </Text>
          <Text fontSize={14} color={colors.sub} ta="center">
            之後配送員送達並經您簽收後，系統會自動從您的 iPASS Money 扣款，
            不需要再掃碼或輸入密碼。
          </Text>
          {/* 授權是從購物車／一鍵下單被導出來的，回去把單送出才算完成 */}
          <Button width="100%" backgroundColor={colors.primary} color="#fff" onPress={() => navigate(returnTo)}>
            {returnTo.startsWith("/kiosk")
              ? "回到訂單繼續送出"
              : returnTo.startsWith("/gasShop/payment")
                ? "回到付款方式繼續下單"
                : "回到購物車送出訂單"}
          </Button>
        </>
      )}

      {state === "fail" && (
        <>
          <RiCloseCircleFill size={64} color={colors.danger} />
          <Text fontSize={20} fontWeight="700" color={colors.text}>
            授權未完成
          </Text>
          <Text fontSize={14} color={colors.sub} ta="center">
            {message || "請重新選擇付款方式，或改用貨到付款。"}
          </Text>
          <Button width="100%" backgroundColor={colors.primary} color="#fff" onPress={() => navigate("/")}>
            回到首頁
          </Button>
        </>
      )}
    </YStack>
  );
}
